
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '../../components/button/button.component';
import { InputComponent } from '../../components/input/input.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import TYPES from '../../constants/types';
import { MatCheckbox } from '@angular/material/checkbox';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { Psychologist } from '../../models/psychologists';
import { PsychologistService } from '../../services/psychologist.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { TextAreaComponent } from '../../components/text-area/text-area.component';

@Component({
  selector: 'app-psychologist',
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    TranslatePipe,
    MatDatepickerModule,
    MatInputModule,
    MatCheckbox,
    MatSelectModule,
    TextAreaComponent
  ],
  templateUrl: './psychologist.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './psychologist.component.scss'
})
export class PsychologistComponent {

  registerForm: FormGroup;
  BUTTON_TYPES: any;
  INFO_SCREEN: any;
  COUNTRIES:any;
  TABLES: any;
  LANGUAGES: any;
  councilNumberControl: UntypedFormControl;
  councilCountryControl: UntypedFormControl;
  sessionLanguageControl: UntypedFormControl;
  descriptionControl: UntypedFormControl;
  acceptInputIsLegitCheckControl: UntypedFormControl;
  isRegisterButtonDisabled:boolean;
  translate: TranslateService = inject(TranslateService);
  authService: AuthService = inject(AUTH_SERVICE);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  snackBar: MatSnackBar = inject(MatSnackBar);
  params: Params | undefined;
  isEditing: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.councilNumberControl = new UntypedFormControl('', [Validators.required]);
    this.councilCountryControl = new UntypedFormControl('', [Validators.required]);
    this.sessionLanguageControl = new UntypedFormControl('', [Validators.required]);
    this.descriptionControl = new UntypedFormControl('', []);
    this.acceptInputIsLegitCheckControl = new UntypedFormControl('', [Validators.requiredTrue]);

    this.registerForm = this.formBuilder.group({
      councilNumber: this.councilNumberControl,
      councilCountry: this.councilCountryControl,
      sessionLanguage: this.sessionLanguageControl,
      description: this.descriptionControl,
      acceptInputIsLegitCheck: this.acceptInputIsLegitCheckControl,
    });

    this.isRegisterButtonDisabled = this.registerForm.invalid;
    this.registerForm.statusChanges.subscribe(
      (event => {
        this.isRegisterButtonDisabled = this.registerForm.invalid;
      })
    );

    this.BUTTON_TYPES = TYPES.UI.BUTTON;
    this.INFO_SCREEN = TYPES.UI.INFO_SCREEN;
    this.TABLES = TYPES.DATABASE.TABLES;
    this.LANGUAGES = TYPES.LANGUAGES;
    
    this.COUNTRIES = TYPES.APPLICATION.COUNTRIES.map(
      (item) => {
        item.name = this.translate.instant(item.name);
        return item;
      }
    );
    this.COUNTRIES.sort((a:any,b:any) => a.name.localeCompare(b.name));

    this.route.queryParams.subscribe(params => {
      this.params = params;

      if(this.params && this.params['edit']){
        const psychologist = this.psychologistService.psychologist();

        if(psychologist){
          this.isEditing = true;
          this.councilNumberControl.patchValue(psychologist?.council_number);
          
          const councilCountry = this.COUNTRIES.find((item:any)=>{return item.code === psychologist?.council_country});
          this.councilCountryControl.patchValue(councilCountry);

          this.sessionLanguageControl.patchValue(psychologist.session_languages);

          this.descriptionControl.patchValue(psychologist.service_description);
        }
      }
    });

  }

  async requestRegister() {
    if(!this.isFormInvalid()){
      const psychologist: Partial<Psychologist> = {
        first_name: this.authService.user()?.first_name,
        last_name: this.authService.user()?.last_name,
        email: this.authService.user()?.email,
        phone_number: this.authService.user()?.phone_number,
        council_number: this.councilNumberControl.value,
        council_country: this.councilCountryControl.value.code,
        session_languages: this.sessionLanguageControl.value,
        service_description: this.descriptionControl.value,
        status: TYPES.DATABASE.STATUS.WAITING_VALIDATION['code'],
        user_id: this.authService.user()?.id,
        subscription_plan: TYPES.DATABASE.PLANS.STARTER.code,
        vip: false
      }       
      
      const psycho = await this.psychologistService.create(this.TABLES.PSYCHOLOGISTS, psychologist);

      if(psycho.id && psycho.user_id){
        const message:string = this.translate.instant('info.psychologist_requested');
        const action:string = this.translate.instant('buttons.dismiss');
        this.snackBar.open(message, action);
        this.router.navigate(['/home'], { replaceUrl: true });
      }

    }else{
      this.registerForm.markAllAsTouched();
      this.registerForm.updateValueAndValidity();
    }
  }

  async updateProfile(){
    if(!this.isFormInvalid()){
      const psychologistId = this.psychologistService.psychologist()?.id;
      if(psychologistId){
        await this.psychologistService.update(
          this.TABLES.PSYCHOLOGISTS,
          psychologistId,
          {
            council_number: this.councilNumberControl.value,
            council_country: this.councilCountryControl.value.code,
            session_languages: this.sessionLanguageControl.value,
            service_description: this.descriptionControl.value
          }
        );

        const message:string = this.translate.instant('info.psychologist_updated');
        const action:string = this.translate.instant('buttons.dismiss');
        this.snackBar.open(message, action);
        this.router.navigate(['/home'], { replaceUrl: true });        

      }
    }
  }

  cancel()  {
    history.back();
  }

  isFormInvalid(): boolean{
    return this.registerForm.invalid
  }

  autocompleteDisplayFn(item:any): string {
    return item.name
  }

}
